import TenantComponents from '@components';
import tenantRoutes from '@routes';
import tenantTheme from '@theme';
import tenantUtils from '@utils';
import { Avatar } from 'antd';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button, Card, Flex, Icon, Skeleton } from '../../common';
import { formatWholeNumber } from '../../../utility/utility';
import { getInitials } from '../../listing/change-owner/assignee-radio-grid';
import { getTimeDateString } from '../../../utility/date';
import {
  QUOTA_OTHERS_KEY,
  canBuyQuota,
  canManageAgencyQuota,
  getQuotaPool,
  isQuotaAgencyView,
} from '../../../utility/quota';
import { useRouteNavigate } from '../../../hooks';
import { useGetQuotaCreditsWidgetDataQuery } from '../../../apis/quotaCredits';
import QuotaProductCards from './product-cards';
import QuotaProductsDrawer from './quota-products-drawer';
import { AgencyBlock, AgencyHeaderGrid, Chip, MobilePool, PoolLegend, StackBar, Swatch } from './styled';

// The agency pool split into not assigned / with agents / used: a bar and its legend.
export const QuotaPoolSplit = ({ totals }) => {
  const { t } = useTranslation();
  const segments = [
    { label: t('Not assigned'), value: totals.notAssigned, color: tenantTheme['primary-color'] },
    { label: t('With agents'), value: totals.withAgents, color: tenantTheme['primary-light-2'] },
    { label: t('quota_used'), value: totals.used, color: tenantTheme['border-color-normal'] },
  ];
  return (
    <>
      <StackBar $height={8}>
        {segments.map((item) => (
          <i key={item.label} style={{ flex: `${item.value || 0} 1 0`, background: item.color }} />
        ))}
      </StackBar>
      <PoolLegend>
        {segments.map((item) => (
          <span key={item.label}>
            <Swatch $color={item.color} /> {item.label}
            <b>{formatWholeNumber(item.value)}</b>
          </span>
        ))}
      </PoolLegend>
    </>
  );
};

// Agency Staff header, one card: the agency (logo or initials, name, email, users, package) and its products.
// A product opens the Available Products drawer; its Assign to Staff calls `onAssign`.
const AgencyQuotaHeader = ({ agencyDetails, loading: agencyLoading, onAssign }) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.app.loginUser.user);
  const isMobile = useSelector((state) => state.app.AppConfig.isMobile);
  const agency = user?.agency;
  const drawerRef = useRef();
  const infoDrawerRef = useRef();
  const [focus, setFocus] = useState(null);
  const navigate = useRouteNavigate();

  const { data, isLoading } = useGetQuotaCreditsWidgetDataQuery(
    { platform_id: [user?.platforms?.[0]?.id] },
    { skip: !user?.id, refetchOnMountOrArgChange: true },
  );
  const isAgencyView = isQuotaAgencyView(data);
  const name = tenantUtils.getLocalisedString(agency, 'name');
  const usersCount = agencyDetails?.list?.length;
  const logo = agencyDetails?.agency_logo;

  const openDrawer = (slug) => {
    setFocus(slug ? { slug, at: Date.now() } : null);
    drawerRef.current?.openDrawer();
  };

  // Mobile: the agency pool in one line, the split, then a chip per product (not assigned).
  const renderMobilePool = () => (
    <MobilePool>
      <small className="pool-cap">{t('Products in your package')}</small>
      <div className="pool-big">
        <b>{formatWholeNumber(data.totals.notAssigned)}</b> {t('not assigned yet')} ·{' '}
        {t('of {{total}}', { total: formatWholeNumber(data.totals.total) })}
      </div>
      <QuotaPoolSplit totals={data.totals} />
      <div className="pool-chips">
        {data.core.map((product) => (
          <Chip as="button" type="button" $button key={product.slug} onClick={() => openDrawer(product.slug)}>
            <Icon icon={product.icon} size="14px" color={product.iconColor} />
            <b>{formatWholeNumber(getQuotaPool(product))}</b> {t(product.name)}
          </Chip>
        ))}
        {!!data.others.length && (
          <Chip as="button" type="button" $button onClick={() => openDrawer(QUOTA_OTHERS_KEY)}>
            <Icon icon="BsGrid" size="13px" color={tenantTheme['gray-dark-color']} />
            <b>{formatWholeNumber(getQuotaPool(data.othersTotals))}</b> {t('Add-ons')}
          </Chip>
        )}
      </div>
      <Button type="default" className="w-100" onClick={() => openDrawer()}>
        {t('Available products')}
      </Button>
    </MobilePool>
  );

  return (
    <Card style={{ border: 0 }} bodyStyle={{ padding: isMobile ? 16 : 20 }}>
      <AgencyHeaderGrid $mobile={isMobile}>
        {agencyLoading ? (
          <Skeleton style={{ height: 72, width: '100%' }} />
        ) : (
          <AgencyBlock>
            <Avatar shape="square" src={logo} className="agency-avatar">
              {getInitials(name)}
            </Avatar>
            <div className="agency-text">
              <b className="agency-name">{name}</b>
              <Flex wrap align="center" gap="4px 12px" className="agency-meta">
                {!!agency?.email && (
                  <span>
                    <Icon icon="MdEmail" size="13px" color={tenantTheme['text-color-secondary']} /> {agency.email}
                  </span>
                )}
                {!!usersCount && (
                  <span>
                    <Icon icon="FiUsers" size="13px" color={tenantTheme['text-color-secondary']} />{' '}
                    {t('{{count}} users', { count: usersCount })}
                  </span>
                )}
              </Flex>
              {!!data?.package?.name && (
                <Chip className="mt-8 agency-package">
                  <Icon icon="BsBoxSeam" size="14px" color={tenantTheme['primary-color']} />
                  <b>{t('{{name}} package', { name: t(data.package.name) })}</b>
                  {!!data.package.end_date &&
                    `· ${t('ends {{date}}', { date: getTimeDateString(data.package.end_date, undefined, true) })}`}
                </Chip>
              )}
              <div>
                <Button
                  type="link"
                  className="p-0 mt-8"
                  style={{ height: 'auto', textDecoration: 'underline' }}
                  onClick={() => infoDrawerRef.current?.openDrawer(true)}
                >
                  {t('What are Products?')}
                </Button>
              </div>
            </div>
          </AgencyBlock>
        )}
        {isLoading ? (
          <Skeleton style={{ height: isMobile ? 120 : 180, width: '100%' }} />
        ) : isMobile ? (
          !!data && renderMobilePool()
        ) : (
          <QuotaProductCards data={data} isAgencyView={isAgencyView} onOpen={openDrawer} />
        )}
      </AgencyHeaderGrid>
      <QuotaProductsDrawer
        ref={drawerRef}
        data={data}
        focus={focus}
        isAgencyView={isAgencyView}
        canManage={canManageAgencyQuota(user)}
        canBuy={canBuyQuota(user)}
        onBuy={() => navigate(tenantRoutes.app('', false, user)?.prop_shop?.path)}
        onAssign={() => {
          drawerRef.current?.closeDrawer();
          onAssign();
        }}
      />
      <TenantComponents.QuotaInfoDrawer ref={infoDrawerRef} />
    </Card>
  );
};

export default AgencyQuotaHeader;
